import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { DynamicDialogModule, DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// Local imports
import { User } from '../users/users.component';
import { SuccessDialogComponent, SuccessDialogData } from '../../../shared/components/success-dialog.component';

@Component({
  selector: 'app-user-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    MessageModule,
    SkeletonModule,
    ToastModule,
    DynamicDialogModule
  ],
  providers: [DialogService, MessageService],
  templateUrl: './user-edit.component.html',
  styleUrl: './user-edit.component.css'
})
export class UserEditComponent implements OnInit, OnDestroy {
  // Loading states
  loading = signal(true);
  submitting = signal(false);
  
  userId: string | null = null;
  isEditMode = false;
  ref: DynamicDialogRef | undefined;
  private timeout?: number;
  private submitTimeout?: number;

  // Define individual form controls
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  usernameControl = new FormControl('', [Validators.required, Validators.minLength(3)]);
  
  // Create form group using the individual controls
  userForm = new FormGroup({
    email: this.emailControl,
    username: this.usernameControl
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private dialogService: DialogService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = this.userId !== null;

    if (this.isEditMode && this.userId) {
      this.loadUser(this.userId);
    } else {
      // For create mode, just set loading to false after a brief delay
      this.timeout = window.setTimeout(() => {
        this.loading.set(false);
      }, 500);
    }
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }
  }

  private loadUser(userId: string): void {
    if (this.timeout) clearTimeout(this.timeout);
    
    this.loading.set(true);
    
    this.timeout = window.setTimeout(() => {
      // TODO: Replace with actual service call
      // Mock data for now - in real app, you would fetch from a service
      const mockUser: User = {
        id: parseInt(userId),
        email: 'user@example.com',
        username: 'exampleuser'
      };

      // Populate form with user data
      this.userForm.patchValue({
        email: mockUser.email,
        username: mockUser.username
      });
      
      this.loading.set(false);
    }, 2000);
  }

  onSubmit(): void {
    if (this.userForm.valid && !this.submitting()) {
      this.submitting.set(true);
      const formValue = this.userForm.value;
      console.log('Form submitted:', formValue);
      
      // Simulate save operation with loading state
      this.submitTimeout = window.setTimeout(() => {
        // Show success toast
        const title = this.isEditMode 
          ? this.translateService.instant('userEdit.success.editTitle')
          : this.translateService.instant('userEdit.success.createTitle');
        
        const message = this.isEditMode
          ? this.translateService.instant('userEdit.success.editMessage')
          : this.translateService.instant('userEdit.success.createMessage');

        this.messageService.add({
          severity: 'success',
          summary: title,
          detail: message,
          life: 4000
        });
        
        this.submitting.set(false);
        
        // Navigate back to users page immediately
        this.router.navigate(['/users']);
      }, 2000); // 2 second loading simulation
    } else if (!this.userForm.valid) {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.router.navigate(['/users']);
  }

  private showSuccessDialog(): void {
    const title = this.isEditMode 
      ? this.translateService.instant('userEdit.success.editTitle')
      : this.translateService.instant('userEdit.success.createTitle');
    
    const message = this.isEditMode
      ? this.translateService.instant('userEdit.success.editMessage')
      : this.translateService.instant('userEdit.success.createMessage');

    const data: SuccessDialogData = {
      title,
      message,
      buttonLabel: this.translateService.instant('common.ok')
    };

    this.ref = this.dialogService.open(SuccessDialogComponent, {
      header: '',
      width: '400px',
      modal: true,
      closable: false,
      data: data
    });

    this.ref.onClose.subscribe(() => {
      // Navigate back to users page when dialog is closed
      this.router.navigate(['/users']);
    });
  }

  getEmailErrorMessage(): string {
    if (this.emailControl.errors?.['required']) {
      return this.translateService.instant('userEdit.errors.emailRequired');
    }
    if (this.emailControl.errors?.['email']) {
      return this.translateService.instant('userEdit.errors.emailInvalid');
    }
    return '';
  }

  getUsernameErrorMessage(): string {
    if (this.usernameControl.errors?.['required']) {
      return this.translateService.instant('userEdit.errors.usernameRequired');
    }
    if (this.usernameControl.errors?.['minlength']) {
      return this.translateService.instant('userEdit.errors.usernameMinLength');
    }
    return '';
  }

  private markFormGroupTouched(): void {
    this.emailControl.markAsTouched();
    this.usernameControl.markAsTouched();
  }
}